import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Card,
  EmptyState,
  Field,
  PageBody,
  PageHeader,
  Select,
  StatusPill,
  TextArea,
  TextInput,
} from "../../components/ui";
import { ApiError, api, request } from "../../lib/api";
import {
  DEFAULT_SCRIPT_VALUE,
  campaignSlugToScriptValue,
  mergeScriptOptions,
  parseScriptValue,
  type ScriptOption,
} from "../../lib/scriptOptions";
import { useStore } from "../../lib/store";

type CampaignStatus = "idle" | "running" | "paused" | "done" | string;

interface CampaignContact {
  phone: string;
  customer_name: string;
  extra?: Record<string, unknown>;
}

interface Campaign {
  id: string;
  name: string;
  script_slug: string;
  delay_s: number;
  max_attempts: number;
  status: CampaignStatus;
  contacts: CampaignContact[];
  contact_count?: number;
}

interface CampaignReport {
  campaign?: Campaign;
  attempts: unknown[];
  outcomes: Record<string, number>;
  attempt_count: number;
  contact_count: number;
  avg_duration_ms: number | null;
  script_slug?: string;
  delay_s?: number;
  max_attempts?: number;
}

interface ContactDraft {
  phone: string;
  customer_name: string;
  extra: string;
}

const PACING_COPY =
  "Pacing is stored here. Operators still place calls from New call. No auto-dial in this version.";

const EMPTY_CONTACT: ContactDraft = { phone: "", customer_name: "", extra: "" };

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (Array.isArray(record.campaigns)) return record.campaigns;
  if (Array.isArray(record.items)) return record.items;
  if (Array.isArray(record.scripts)) return record.scripts;
  return [];
}

function parseExtra(raw: string): Record<string, unknown> | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed: unknown = JSON.parse(trimmed);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Contact extra must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}

function parseContact(raw: unknown): CampaignContact | null {
  const row = asRecord(raw);
  const phone = String(row.phone ?? row.dest ?? "").trim();
  const customer_name = String(row.customer_name ?? row.name ?? "").trim();
  if (!phone && !customer_name) return null;
  const extraRaw = row.extra;
  const extra =
    extraRaw && typeof extraRaw === "object" && !Array.isArray(extraRaw)
      ? (extraRaw as Record<string, unknown>)
      : undefined;
  return extra ? { phone, customer_name, extra } : { phone, customer_name };
}

function parseCampaign(raw: unknown): Campaign | null {
  const row = asRecord(raw);
  const id = String(row.id ?? "").trim();
  if (!id) return null;
  const contacts = Array.isArray(row.contacts)
    ? row.contacts.map(parseContact).filter((item): item is CampaignContact => item !== null)
    : [];
  return {
    id,
    name: String(row.name ?? "").trim() || id,
    script_slug: String(row.script_slug ?? "office").trim() || "office",
    delay_s: Number(row.delay_s ?? 8) || 0,
    max_attempts: Number(row.max_attempts ?? 3) || 1,
    status: String(row.status ?? "idle") || "idle",
    contacts,
    contact_count: Number(row.contact_count ?? contacts.length) || contacts.length,
  };
}

function parseCampaigns(raw: unknown): Campaign[] {
  return asList(raw)
    .map(parseCampaign)
    .filter((item): item is Campaign => item !== null);
}

function parseReport(raw: unknown): CampaignReport {
  const row = asRecord(raw);
  const outcomesRaw = asRecord(row.outcomes);
  const outcomes: Record<string, number> = {};
  for (const [key, value] of Object.entries(outcomesRaw)) {
    const n = Number(value);
    if (Number.isFinite(n)) outcomes[key] = n;
  }
  const attempts = Array.isArray(row.attempts) ? row.attempts : [];
  const avg = row.avg_duration_ms;
  return {
    campaign: parseCampaign(row.campaign) ?? undefined,
    attempts,
    outcomes,
    attempt_count: Number(row.attempt_count ?? attempts.length) || attempts.length,
    contact_count: Number(row.contact_count ?? 0) || 0,
    avg_duration_ms: typeof avg === "number" && Number.isFinite(avg) ? avg : null,
    script_slug: row.script_slug ? String(row.script_slug) : undefined,
    delay_s: typeof row.delay_s === "number" ? row.delay_s : undefined,
    max_attempts: typeof row.max_attempts === "number" ? row.max_attempts : undefined,
  };
}

function statusTone(status: CampaignStatus): "teal" | "ok" | "fail" | "live" | "mute" {
  const key = String(status).toLowerCase();
  if (key === "running") return "live";
  if (key === "done") return "ok";
  if (key === "paused") return "fail";
  if (key === "idle") return "teal";
  return "mute";
}

function extraToDraft(extra: Record<string, unknown> | undefined): string {
  if (!extra || Object.keys(extra).length === 0) return "";
  try {
    return JSON.stringify(extra);
  } catch {
    return "";
  }
}

/**
 * Campaign pacing manager. Stores delay / attempts / contacts.
 * Start and Pause POST status only — never originate or loop-dial.
 */
export function Campaigns() {
  const { toast } = useStore();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [scriptKey, setScriptKey] = useState(DEFAULT_SCRIPT_VALUE);
  const [delayS, setDelayS] = useState("8");
  const [maxAttempts, setMaxAttempts] = useState("3");
  const [contacts, setContacts] = useState<ContactDraft[]>([{ ...EMPTY_CONTACT }]);
  const [scriptOptions, setScriptOptions] = useState<ScriptOption[]>([]);
  const [reportsFor, setReportsFor] = useState<string | null>(null);
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [reportMissing, setReportMissing] = useState(false);

  async function refresh() {
    try {
      const data = await request<unknown>("/api/campaigns");
      setUnavailable(false);
      setCampaigns(parseCampaigns(data));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setUnavailable(true);
        setCampaigns([]);
        return;
      }
      toast(err instanceof Error ? err.message : "Could not load campaigns", "error");
      setCampaigns([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [packs, ivr] = await Promise.allSettled([api.packs(), api.listIvr()]);
      if (cancelled) return;
      const packList = packs.status === "fulfilled" ? packs.value.packs ?? [] : [];
      const ivrList = ivr.status === "fulfilled" ? ivr.value.scripts ?? [] : [];
      setScriptOptions(mergeScriptOptions(packList, ivrList));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function resetForm() {
    setEditingId(null);
    setName("");
    setScriptKey(DEFAULT_SCRIPT_VALUE);
    setDelayS("8");
    setMaxAttempts("3");
    setContacts([{ ...EMPTY_CONTACT }]);
  }

  function loadCampaign(campaign: Campaign) {
    setEditingId(campaign.id);
    setName(campaign.name);
    setScriptKey(campaignSlugToScriptValue(campaign.script_slug || "office", scriptOptions));
    setDelayS(String(campaign.delay_s ?? 8));
    setMaxAttempts(String(campaign.max_attempts ?? 3));
    const rows = campaign.contacts.map((item) => ({
      phone: item.phone,
      customer_name: item.customer_name,
      extra: extraToDraft(item.extra),
    }));
    setContacts(rows.length ? rows : [{ ...EMPTY_CONTACT }]);
  }

  function patchContact(index: number, partial: Partial<ContactDraft>) {
    setContacts((prev) => prev.map((row, i) => (i === index ? { ...row, ...partial } : row)));
  }

  const bodyPreview = useMemo(
    () => ({
      name: name.trim(),
      script_slug: parseScriptValue(scriptKey).slug || "office",
      delay_s: Number.parseInt(delayS, 10),
      max_attempts: Number.parseInt(maxAttempts, 10),
    }),
    [name, scriptKey, delayS, maxAttempts],
  );

  async function saveCampaign() {
    if (!bodyPreview.name) {
      toast("Campaign name is required", "error");
      return;
    }
    if (!Number.isInteger(bodyPreview.delay_s) || bodyPreview.delay_s < 0) {
      toast("Delay must be a whole number of seconds", "error");
      return;
    }
    if (!Number.isInteger(bodyPreview.max_attempts) || bodyPreview.max_attempts < 1) {
      toast("Max attempts must be at least 1", "error");
      return;
    }
    const parsedContacts: CampaignContact[] = [];
    try {
      for (const row of contacts) {
        const phone = row.phone.trim();
        const customer_name = row.customer_name.trim();
        if (!phone && !customer_name && !row.extra.trim()) continue;
        if (!phone || !customer_name) {
          toast("Each contact needs phone and customer name", "error");
          return;
        }
        const extra = parseExtra(row.extra);
        parsedContacts.push(extra ? { phone, customer_name, extra } : { phone, customer_name });
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : "Invalid extra JSON", "error");
      return;
    }
    const payload: Record<string, unknown> = {
      ...bodyPreview,
      contacts: parsedContacts,
    };
    setBusy(true);
    try {
      if (editingId) {
        await request(`/api/campaigns/${encodeURIComponent(editingId)}`, {
          method: "PUT",
          body: payload,
        });
        toast("Campaign updated", "ok");
      } else {
        await request("/api/campaigns", { body: payload });
        toast("Campaign saved", "ok");
      }
      resetForm();
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, status: "running" | "paused") {
    // Status only. Never originate, never loop-dial, never hit /api/session/originate.
    const path =
      status === "running"
        ? `/api/campaigns/${encodeURIComponent(id)}/start`
        : `/api/campaigns/${encodeURIComponent(id)}/pause`;
    setBusy(true);
    try {
      await request(path, { body: {} });
      toast(status === "running" ? "Campaign marked running" : "Campaign paused", "ok");
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Status update failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function loadReports(id: string) {
    setReportsFor(id);
    setReport(null);
    setReportMissing(false);
    try {
      const data = await request<unknown>(`/api/campaigns/${encodeURIComponent(id)}/reports`);
      setReport(parseReport(data));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setReportMissing(true);
        return;
      }
      toast(err instanceof Error ? err.message : "Could not load reports", "error");
      setReportMissing(true);
    }
  }

  if (unavailable) {
    return (
      <PageBody className="mx-auto flex w-full max-w-3xl flex-col">
        <PageHeader kicker="Campaign" title="📋 Campaigns" />
        <p className="mb-4 max-w-2xl text-sm text-mute">{PACING_COPY}</p>
        <EmptyState
          title="Campaigns aren’t on this build yet"
          body="The pacing API returned 404. Delay, attempts, and reports will live here when the endpoint is enabled. Operators still place calls from New call."
        />
      </PageBody>
    );
  }

  return (
    <PageBody className="mx-auto flex w-full max-w-4xl flex-col">
      <PageHeader
        kicker="Campaign"
        title="📋 Campaigns"
        actions={
          <Button variant="ghost" disabled={busy} onClick={() => resetForm()}>
            New
          </Button>
        }
      />
      <p className="mb-4 max-w-2xl text-sm text-mute">{PACING_COPY}</p>

      <Card className="p-5">
        <h2 className="mb-4 text-base font-medium text-ink">
          {editingId ? "✏️ Edit campaign" : "✨ New campaign"}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name">
            <TextInput
              value={name}
              placeholder="Morning UK mobiles"
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Script" hint="Pack = office-flow voice lines. Graph = custom flow. Never both.">
            {scriptOptions.length > 0 ? (
              <Select
                value={scriptKey}
                disabled={busy}
                onChange={(event) => setScriptKey(event.target.value)}
              >
                {!scriptOptions.some((item) => item.value === scriptKey) ? (
                  <option value={scriptKey}>{parseScriptValue(scriptKey).slug}</option>
                ) : null}
                {scriptOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            ) : (
              <TextInput
                value={parseScriptValue(scriptKey).slug}
                placeholder="default"
                disabled={busy}
                onChange={(event) =>
                  setScriptKey(campaignSlugToScriptValue(event.target.value, scriptOptions))
                }
              />
            )}
          </Field>
          <Field label="Delay (seconds)">
            <TextInput
              inputMode="numeric"
              value={delayS}
              disabled={busy}
              onChange={(event) => setDelayS(event.target.value)}
            />
          </Field>
          <Field label="Max attempts">
            <TextInput
              inputMode="numeric"
              value={maxAttempts}
              disabled={busy}
              onChange={(event) => setMaxAttempts(event.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
              Contacts
            </div>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setContacts((prev) => [...prev, { ...EMPTY_CONTACT }])}
            >
              Add contact
            </Button>
          </div>
          <div className="space-y-3">
            {contacts.map((row, index) => (
              <div key={`contact-${index}`} className="grid gap-2 md:grid-cols-3">
                <TextInput
                  placeholder="Phone"
                  value={row.phone}
                  disabled={busy}
                  onChange={(event) => patchContact(index, { phone: event.target.value })}
                />
                <TextInput
                  placeholder="Customer name"
                  value={row.customer_name}
                  disabled={busy}
                  onChange={(event) => patchContact(index, { customer_name: event.target.value })}
                />
                <div className="flex gap-2">
                  <TextInput
                    placeholder='Extra JSON (optional) {"ref":"1"}'
                    className="flex-1 font-mono"
                    value={row.extra}
                    disabled={busy}
                    onChange={(event) => patchContact(index, { extra: event.target.value })}
                  />
                  {contacts.length > 1 ? (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setContacts((prev) => prev.filter((_, i) => i !== index))}
                    >
                      ✕
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          {editingId ? (
            <Button variant="ghost" disabled={busy} onClick={() => resetForm()}>
              Cancel
            </Button>
          ) : null}
          <Button variant="primary" disabled={busy} onClick={() => void saveCampaign()}>
            {editingId ? "Update" : "Save campaign"}
          </Button>
        </div>
      </Card>

      {campaigns.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No campaigns yet"
            body="Create one to store delay and retry pacing. Operators still place each call from New call."
          />
        </div>
      ) : (
        <div className="mt-4 min-h-0 overflow-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface text-[11px] uppercase tracking-[0.16em] text-mute">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Script</th>
                <th className="px-4 py-3">Delay</th>
                <th className="px-4 py-3">Attempts</th>
                <th className="px-4 py-3">Contacts</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const running = String(campaign.status).toLowerCase() === "running";
                return (
                  <tr key={campaign.id} className="border-t border-border hover:bg-white/3">
                    <td className="px-4 py-2.5 text-ink">{campaign.name}</td>
                    <td className="px-4 py-2.5 font-mono text-ink-2">{campaign.script_slug}</td>
                    <td className="px-4 py-2.5 font-mono tabular-nums">{campaign.delay_s}s</td>
                    <td className="px-4 py-2.5 font-mono tabular-nums">{campaign.max_attempts}</td>
                    <td className="px-4 py-2.5 font-mono tabular-nums">
                      {campaign.contact_count ?? campaign.contacts.length}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill tone={statusTone(campaign.status)}>{campaign.status}</StatusPill>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-2">
                        <Button variant="ghost" disabled={busy} onClick={() => loadCampaign(campaign)}>
                          Edit
                        </Button>
                        {running ? (
                          <Button
                            variant="subtle"
                            disabled={busy}
                            onClick={() => void setStatus(campaign.id, "paused")}
                          >
                            ⏸ Pause
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            disabled={busy}
                            onClick={() => void setStatus(campaign.id, "running")}
                          >
                            ▶ Start
                          </Button>
                        )}
                        <Button variant="ghost" disabled={busy} onClick={() => void loadReports(campaign.id)}>
                          📊 Reports
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reportsFor ? (
        <Card className="mt-4 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-base font-medium text-ink">📊 Reports</h2>
            <Button
              variant="ghost"
              onClick={() => {
                setReportsFor(null);
                setReport(null);
                setReportMissing(false);
              }}
            >
              Close
            </Button>
          </div>
          {reportMissing ? (
            <EmptyState
              title="No reports yet"
              body="This campaign has no attempt log, or the reports endpoint is not on this build."
            />
          ) : report ? (
            <div className="space-y-3 text-sm">
              <p className="text-mute">
                {report.attempt_count} attempts · {report.contact_count} contacts
                {report.avg_duration_ms != null ? ` · avg ${report.avg_duration_ms} ms` : ""}
                {report.script_slug ? ` · script ${report.script_slug}` : ""}
              </p>
              {Object.keys(report.outcomes).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(report.outcomes).map(([key, count]) => (
                    <StatusPill key={key} tone="mute">
                      {key}: {count}
                    </StatusPill>
                  ))}
                </div>
              ) : (
                <p className="text-mute">No outcomes recorded.</p>
              )}
              {report.attempts.length > 0 ? (
                <TextArea
                  readOnly
                  className="min-h-40"
                  value={report.attempts
                    .map((row) => {
                      try {
                        return JSON.stringify(row);
                      } catch {
                        return String(row);
                      }
                    })
                    .join("\n")}
                />
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-mute">Loading reports…</p>
          )}
        </Card>
      ) : null}
    </PageBody>
  );
}
